"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _airbnbPropTypes = _interopRequireDefault(require("airbnb-prop-types"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _ButtonBase = _interopRequireDefault(require("../Button/ButtonBase"));
var _Icon = _interopRequireDefault(require("../Icon"));
var _StackView = require("../StackView");
var _ViewportProvider = require("../ViewportProvider");
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _pressability = require("../utils/pressability");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const [selectItemProps, selectedItemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.focusHandlerProps, _utils.pressProps, _utils.viewProps]);
const separateIconTextTokens = (_ref, returnRest) => {
  let {
    icon,
    iconPosition,
    iconSpace,
    iconSize,
    iconColor,
    iconBackground,
    iconBorderRadius,
    iconAlignSelf,
    iconPadding,
    iconTranslateX,
    iconTranslateY,
    ...rest
  } = _ref;
  return returnRest ? rest : {
    icon,
    iconPosition,
    iconSpace,
    iconWrapperStyle: {
      backgroundColor: iconBackground,
      borderRadius: iconBorderRadius,
      alignSelf: iconAlignSelf,
      padding: iconPadding,
      ..._Platform.default.select({
        // TODO: https://github.com/telus/universal-design-system/issues/487
        web: {
          transition: 'color 200ms, background 200ms'
        }
      })
    },
    iconTokens: {
      size: iconSize,
      color: iconColor,
      translateX: iconTranslateX,
      translateY: iconTranslateY
    }
  };
};
const selectIconTextTokens = tokens => separateIconTextTokens(tokens, false);
const selectNonIconTextTokens = tokens => separateIconTextTokens(tokens, true);
const Tags = /*#__PURE__*/_react.default.forwardRef((_ref2, ref) => {
  let {
    variant,
    tokens,
    items = [],
    values,
    initialValues,
    maxValues,
    onChange,
    readOnly = false,
    inactive = false,
    accessibilityRole = _Platform.default.select({
      web: 'group',
      default: 'none'
    }),
    ...rest
  } = _ref2;
  const viewport = (0, _ViewportProvider.useViewport)();
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('Tags', tokens, variant, {
    viewport
  });
  const stackTokens = (0, _utils.selectTokens)('StackView', themeTokens);
  const {
    direction,
    space
  } = themeTokens;
  const getItemTokens = (0, _ThemeProvider.useThemeTokensCallback)('TagsItem', tokens, variant);
  const getButtonTokens = buttonState =>
  // Remove icon-text-related tokens, since we want to handle them ourselves, not use ButtonBase's handling
  (0, _utils.selectTokens)('Button', selectNonIconTextTokens(getItemTokens(buttonState)));
  const {
    currentValues,
    toggleOneValue
  } = (0, _utils.useMultipleInputValues)({
    initialValues,
    values,
    maxValues,
    onChange,
    readOnly
  });
  const selectedProps = selectProps({
    accessibilityRole,
    ...rest
  });
  const itemA11yRole = 'checkbox';
  const uniqueFields = ['id', 'label'];
  if (!(0, _utils.containUniqueFields)(items, uniqueFields)) {
    throw new Error(`Tags items must have unique ${uniqueFields.join(', ')}`);
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_StackView.StackWrap, {
    ref: ref,
    ...selectedProps,
    space: space,
    direction: direction,
    tokens: stackTokens,
    children: items.map((_ref3, index) => {
      let {
        label,
        id = label,
        ref: itemRef,
        ...itemRest
      } = _ref3;
      const isSelected = currentValues.includes(id);

      // Pass an object of relevant component state as first argument for any passed-in press handlers
      const pressHandlers = (0, _pressability.getPressHandlersWithArgs)(rest, [{
        id,
        label,
        currentValues
      }]);
      const handlePress = event => {
        if (pressHandlers.onPress) pressHandlers.onPress(event);
        toggleOneValue(id, event);
      };
      const itemProps = selectItemProps({
        accessibilityState: {
          checked: isSelected
        },
        accessibilityRole: itemA11yRole,
        ..._utils.a11yProps.getPositionInSet(items.length, index),
        ...itemRest
      });
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_ButtonBase.default, {
        ref: itemRef,
        ...pressHandlers,
        onPress: handlePress,
        tokens: getButtonTokens,
        selected: isSelected,
        inactive: inactive,
        ...itemProps,
        children: _ref4 => {
          let {
            textStyles,
            ...buttonState
          } = _ref4;
          // TODO: once Icon/IconButton designs are stable, see if this sort of styling around
          // an icon should go in Icon itself, or possibly via an IconText token set. Related issues:
          // - Icon: https://github.com/telus/universal-design-system/issues/327
          // - IconButton: https://github.com/telus/universal-design-system/issues/281
          // - Token sets: https://github.com/telus/universal-design-system/issues/782

          const itemTokens = getItemTokens(buttonState);
          const {
            iconTokens,
            iconPosition,
            iconSpace,
            iconWrapperStyle,
            icon: IconComponent
          } = selectIconTextTokens(itemTokens);
          const iconContent = IconComponent ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
            style: iconWrapperStyle,
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.default, {
              icon: IconComponent,
              tokens: iconTokens
            })
          }) : null;
          const textContent = /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
            style: textStyles,
            children: label
          });
          return (0, _StackView.getStackedContent)(iconPosition === 'left' ? [iconContent, textContent] : [textContent, iconContent], {
            space: iconSpace,
            direction: 'row'
          });
        }
      }, id);
    })
  });
});
Tags.displayName = 'Tags';
Tags.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _utils.getTokensPropType)('Tags'),
  variant: _utils.variantProp.propType,
  /**
   * If provided, sets a maximum number of items a user may select at once.
   */
  maxValues: _propTypes.default.number,
  /**
   * The options a user may select
   */
  items: _propTypes.default.arrayOf(_propTypes.default.shape({
    ...selectedItemPropTypes,
    /**
     * The text displayed to the user in the button, describing this option,
     * passed to the button as its child.
     */
    label: _propTypes.default.string.isRequired,
    /**
     * An optional accessibility label may be passed to ButtonGroup Buttons
     * which is applied as normal for a React Native accessibilityLabel prop.
     */
    accessibilityLabel: _propTypes.default.string,
    /**
     * An optional unique string may be provided to identify this option,
     * which will be used in code and passed to any onChange function.
     * If not provided, the label is used.
     */
    id: _propTypes.default.string,
    /**
     * An optional ref for one individual Tag button in the tags
     */
    ref: _airbnbPropTypes.default.ref()
  })),
  /**
   * If provided, this function is called when the current selection is changed
   * and is passed an array of the `id`s of all currently selected `items`.
   */
  onChange: _propTypes.default.func,
  /**
   * If the selected item(s) in the button group are to be controlled externally by
   * a parent component, pass an array of strings as well as an `onChange` handler.
   * Passing an array for "values" makes the ButtonGroup a "controlled" component that
   * expects its state to be handled via `onChange` and so doesn't handle it itself.
   */
  values: _propTypes.default.arrayOf(_propTypes.default.string),
  /**
   * If `values` is not passed, making the ButtonGroup an "uncontrolled" component
   * managing its own selected state, a default set of selections may be provided.
   * Changing the `initialValues` does not change the user's selections.
   */
  initialValues: _propTypes.default.arrayOf(_propTypes.default.string),
  /**
   * AccesibilityRole for the Tags
   */
  accessibilityRole: _propTypes.default.string
};
var _default = exports.default = Tags;